/**
 * WEB-2054 — кусок рендерера «БЛОКИ РАЗБОРА РЕФЕРЕНСОВ» (WEB-926, позиции 1-8).
 *
 * Скретч-карта скидки, blur-тизер, график-прогноз, промо-тикет, утверждение с
 * тремя реакциями, карточка предпочтения, полосы «сейчас/цель» и шкала ИМТ.
 *
 * ПОЧЕМУ ОДНОЙ СЕМЬЁЙ. Каждый из восьми тянет СВОЙ компонент рисования (холст
 * скретч-карты, два SVG-графика, шкала) — вместе это самая тяжёлая редкая часть
 * рендерера, и ни один из восьми не встречается в обычной воронке из текста,
 * картинки и вариантов выбора. Отдельным куском на каждый вид делать нельзя:
 * воронка-референс ставит их по три-четыре разом, и это были бы три-четыре
 * лишних запроса к CDN вместо одного.
 *
 * Ветки перенесены из `PublicBlockRendererExtended` без правок разметки: разрез
 * менял только то, откуда они приезжают.
 */
import React from 'react';
import type { PublicExtendedBlockProps } from '@/player/publicExtendedBlockProps';
import { interpolate } from '@/player/interpolate';
import { computedBmiText } from '@/player/computedValues';
import { asRichField, hasRichText } from '@/types/rich-text';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { RichTextContent } from '../RichTextContent';
import { useFormValues } from '../FormValuesContext';
import { backgroundFillColor } from '../blockBackgroundStyle';
import {
  blockTextRoles,
  preferenceTextRoles,
  statementTextRoles,
  textRoleStyle,
} from '../semanticTextRoles';
import { resolvePromoTicketShape } from '../promoTicket';
import { ScratchCardBlock } from '../ScratchCardBlock';
import { BlurTeaserBlock } from '../BlurTeaserBlock';
import { ForecastChartBlock } from '../ForecastChartBlock';
import { PromoTicketBlock } from '../PromoTicketBlock';
import { StatementAgreementBlock } from '../StatementAgreementBlock';
import { PreferenceCardBlock } from '../PreferenceCardBlock';
import { NowGoalBarsBlock } from '../NowGoalBarsBlock';
import { BmiScaleBlock } from '../BmiScaleBlock';
import { withExtendedBlockShell } from '../extendedBlockShell';

export function ReferenceBlocksRenderer({
  block,
  varCtx,
  stylePackId,
  followsExplicitSpacing,
}: PublicExtendedBlockProps): React.ReactElement | null {
  /** F6: apply interpolation to plain text fields when varCtx is present. */
  const interp = (text: string | undefined): string =>
    text == null ? '' : varCtx ? interpolate(text, varCtx) : text;
  /** WEB-672 2a: rich-field render path (mirror of RICH_FIELDS_BY_BLOCK_TYPE). */
  const richText = (value: string | undefined, fallback?: React.ReactNode) => (
    <RichTextContent
      value={asRichField(value)}
      varCtx={varCtx}
      fallback={fallback}
    />
  );
  /** Truthiness gate replacement for rich fields (parity for plain strings). */
  const hasText = (value: string | undefined): boolean =>
    hasRichText(asRichField(value));
  // WEB-222: answer capture — два блока этой семьи отвечающие (statement_agreement,
  // preference_card): они пишут values[id], и он же уходит в SCREEN_ANSWER.
  const { values, setValue } = useFormValues();

  const rendered = ((): React.ReactElement | null => {
    switch (block.type) {
      // WEB-926 позиция 1: скретч-карта скидки (BetterMe). Доступность — внутри
      // компонента: текст скидки в DOM + кнопка-обход рядом с картой.
      case 'scratch_card':
        return (
          <ScratchCardBlock
            coverLabel={interp(block.scratchCoverLabel)}
            revealTitle={interp(block.scratchRevealTitle)}
            revealNote={interp(block.scratchRevealNote)}
            coverColor={block.scratchCoverColor}
            coverPreset={block.scratchCoverPreset}
            coverImageUrl={block.scratchCoverImageUrl}
            thresholdPercent={block.scratchThresholdPercent}
            revealButtonLabel={
              interp(block.scratchRevealButtonLabel).trim() ||
              getPublicQuizStrings(varCtx?.system?.locale).scratchRevealLabel
            }
          />
        );

      // WEB-926 позиция 2: blur-тизер с замочками (Astroline). Размытие —
      // оформление, а не защита: под ним только приманка (см. BlurTeaserBlock).
      case 'blur_teaser': {
        const teaserItems = block.blurTeaserItems ?? [];
        if (teaserItems.length === 0) return null;
        const blurTeaserRoles = blockTextRoles(block, 'blur_teaser');
        return (
          <BlurTeaserBlock
            items={teaserItems.map((item) => ({
              ...item,
              // WEB-2973: подпись строки — тоже текст автора.
              label: interp(item.label),
              teaser: interp(item.teaser),
            }))}
            title={hasText(block.content) ? richText(block.content) : undefined}
            note={interp(block.blurTeaserNote) || undefined}
            strength={block.blurTeaserStrength}
            layout={block.blurTeaserLayout}
            lockedLabel={
              getPublicQuizStrings(varCtx?.system?.locale).blurTeaserLockedLabel
            }
            noteColor={block.blurTeaserNoteColor}
            noteFontSize={block.blurTeaserNoteFontSize}
            lockIconUrl={block.blurTeaserLockIconUrl}
            // WEB-1692 юнит 4: две группы, названные владельцем — текст
            // снаружи плашек и текст внутри них. Подпись-условие снизу правят
            // её собственные поля выше (WEB-1819), второй пары ей не заводим.
            titleRole={blurTeaserRoles.title}
            cardRole={blurTeaserRoles.secondary}
          />
        );
      }

      // WEB-926 позиция 3: график-прогноз (12min). Все подписи — авторские, блок
      // ничего не считает по ответам: это обещание динамики, не расчёт.
      case 'forecast_chart':
        return (
          <ForecastChartBlock
            title={hasText(block.content) ? richText(block.content) : undefined}
            nowLabel={interp(block.forecastNowLabel)}
            nowValue={interp(block.forecastNowValue)}
            goalLabel={interp(block.forecastGoalLabel)}
            axisStartLabel={interp(block.forecastAxisStartLabel)}
            axisEndLabel={interp(block.forecastAxisEndLabel)}
            caption={interp(block.forecastCaption)}
            accentColor={block.forecastAccentColor}
            curve={block.forecastCurve}
            chartLabel={
              getPublicQuizStrings(varCtx?.system?.locale).forecastChartLabel
            }
          />
        );

      // WEB-926 позиция 4: промо-тикет. Форма (перфорация/плашка) — из пака,
      // таймер — общий модуль countdown. Скидку к оплате блок НЕ применяет:
      // сумму списания пользователь видит в окне Stripe (решение PM 2026-07-31).
      case 'promo_ticket': {
        // WEB-1692 юнит 3: заголовок — роль `Title`, примечание — роль
        // `Secondary`; код и таймер названы владельцем отдельно и идут своими
        // полями. Цены под настройку не попадают (пара «было/стало»).
        const ticketRoles = blockTextRoles(block, 'promo_ticket');
        return (
          <PromoTicketBlock
            blockId={block.id}
            titleStyle={ticketRoles.title}
            noteStyle={ticketRoles.secondary}
            codeStyle={textRoleStyle(
              block.promoTicketCodeColor,
              block.promoTicketCodeFontSize,
            )}
            timerStyle={textRoleStyle(
              block.promoTicketTimerColor,
              block.promoTicketTimerFontSize,
            )}
            shape={resolvePromoTicketShape(stylePackId)}
            title={interp(block.promoTicketTitle)}
            code={interp(block.promoTicketCode)}
            note={interp(block.promoTicketNote)}
            oldPrice={interp(block.promoTicketOldPrice)}
            newPrice={interp(block.promoTicketNewPrice)}
            showTimer={block.promoTicketShowTimer}
            targetDate={block.targetDate}
            durationMinutes={block.countdownMinutes}
            timerLabel={
              interp(block.promoTicketTimerLabel).trim() ||
              getPublicQuizStrings(varCtx?.system?.locale).promoTicketTimerLabel
            }
          />
        );
      }

      // WEB-926 позиция 5: утверждение + три реакции (AddMile). ОТВЕЧАЮЩИЙ блок:
      // пишет values[id] слагом disagree|unsure|agree — он же уходит в
      // SCREEN_ANSWER и виден условиям ветвления.
      case 'statement_agreement': {
        const roles = statementTextRoles(block);
        return (
          <StatementAgreementBlock
            blockId={block.id}
            statement={
              hasText(block.content) ? richText(block.content) : undefined
            }
            prompt={interp(block.statementPrompt)}
            labels={{
              disagree: interp(block.statementNegativeLabel),
              unsure: interp(block.statementNeutralLabel),
              agree: interp(block.statementPositiveLabel),
            }}
            showNeutral={block.statementShowNeutral}
            // WEB-1692: две накладки по смысловым ролям текста — утверждение
            // отдельно от строки-вопроса и подписей реакций. Пусто = прежний
            // вид (механика и решения — semanticTextRoles.ts).
            titleStyle={roles.title}
            secondaryStyle={roles.secondary}
            value={String(values[block.id] ?? '')}
            onSelect={(next) => setValue(block.id, next)}
          />
        );
      }

      // WEB-926 позиция 6: бинарная карточка предпочтения (12min). ОТВЕЧАЮЩИЙ
      // блок: пишет values[id] = no|yes.
      case 'preference_card': {
        const roles = preferenceTextRoles(block);
        return (
          <PreferenceCardBlock
            blockId={block.id}
            question={
              hasText(block.content) ? richText(block.content) : undefined
            }
            imageUrl={block.preferenceImageUrl}
            emoji={block.preferenceEmoji}
            caption={interp(block.preferenceCaption)}
            labels={{
              no: interp(block.preferenceNoLabel),
              yes: interp(block.preferenceYesLabel),
            }}
            // WEB-1692: вопрос отдельно от вторичных текстов — подписи под
            // картинкой и подписей обоих ответов (они идут одной настройкой).
            titleStyle={roles.title}
            secondaryStyle={roles.secondary}
            value={String(values[block.id] ?? '')}
            onSelect={(next) => setValue(block.id, next)}
          />
        );
      }

      // WEB-926 позиция 7: сравнение «сейчас/цель» через бары (BetterMe).
      // Значения и длины баров — авторские: «цели» в ответах нет вовсе, а
      // «сейчас» лежит там без единицы и без шкалы. Ответ автор подставляет сам
      // через {{переменную}} в текст значения.
      case 'now_goal_bars': {
        const strings = getPublicQuizStrings(varCtx?.system?.locale);
        const nowGoalRoles = blockTextRoles(block, 'now_goal_bars');
        const rows = (block.nowGoalRows ?? []).map((row) => ({
          ...row,
          label: interp(row.label),
          nowText: interp(row.nowText),
          goalText: interp(row.goalText),
        }));
        return (
          <NowGoalBarsBlock
            title={hasText(block.content) ? richText(block.content) : undefined}
            nowLabel={interp(block.nowGoalNowLabel) || strings.nowGoalNowLabel}
            goalLabel={
              interp(block.nowGoalGoalLabel) || strings.nowGoalGoalLabel
            }
            rows={rows}
            note={interp(block.nowGoalNote) || undefined}
            accentColor={block.nowGoalAccentColor}
            // WEB-1692 юнит 4: заголовок отдельно от подписей полос; разница
            // «сейчас»/«цель» выводится приглушением (вариант А).
            titleRole={nowGoalRoles.title}
            labelsRole={nowGoalRoles.secondary}
          />
        );
      }

      // WEB-926 позиция 8: шкала ИМТ с ползунком (BetterMe). Значение вписывает
      // АВТОР (можно {{переменной}}): пикеры роста и веса пишут в ответ число без
      // единицы (kg/lb-переключатель в ответ не попадает), так что расчёт ИМТ был
      // бы угадыванием единицы — в медицинском контексте это недопустимо.
      // Не число → ползунка нет, шкала остаётся без позиции.
      // Решение владельца №5: `bmiValueSource: 'computed'` — число из ответов
      // роста и веса по формуле эталона (единица в ответе есть: `<id>::unit`
      // с WEB-1301, иначе умолчание блока); посчитать нельзя → пусто, как выше.
      case 'bmi_scale': {
        const bmiRoles = blockTextRoles(block, 'bmi_scale');
        return (
          <BmiScaleBlock
            title={hasText(block.content) ? richText(block.content) : undefined}
            value={
              block.bmiValueSource === 'computed'
                ? computedBmiText(varCtx)
                : interp(block.bmiValue)
            }
            valueLabel={interp(block.bmiValueLabel) || undefined}
            scaleMin={block.bmiScaleMin}
            scaleMax={block.bmiScaleMax}
            zones={(block.bmiZones ?? []).map((zone) => ({
              ...zone,
              label: interp(zone.label),
            }))}
            note={interp(block.bmiNote) || undefined}
            scaleLabel={
              getPublicQuizStrings(varCtx?.system?.locale).bmiScaleLabel
            }
            // WEB-1692 юнит 4: заголовок, метки зон (два состояния — вариант
            // А), число с подписью над ним и текст под метками. Цвета зон
            // шкалы не трогаются: смысловой код медицинской шкалы.
            titleRole={bmiRoles.title}
            zoneLabelsRole={bmiRoles.secondary}
            valueRole={textRoleStyle(
              block.bmiValueColor,
              block.bmiValueFontSize,
            )}
            noteRole={textRoleStyle(block.bmiNoteColor, block.bmiNoteFontSize)}
            // WEB-1677 (возврат приёмки 02.09): «Заливка элемента» доезжает до
            // шкалы ЗНАЧЕНИЕМ, а не переменной поверхности. Своей карточки у
            // блока не было, переменную читал один кружок-ползунок — и только
            // когда авторское значение оказалось числом; у блока по умолчанию
            // (bmiValue: "") читателей не было вовсе, и настройка была мертва.
            // Тот же сборщик цвета, что у обёртки и у баннера срочности, —
            // правда о «hex + плотность → цвет» остаётся одна.
            innerFill={
              block.innerBackground
                ? backgroundFillColor(block.innerBackground)
                : undefined
            }
          />
        );
      }

      default:
        return null;
    }
  })();

  return withExtendedBlockShell(block, rendered, followsExplicitSpacing);
}
